import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject as RxJsSubject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import {
  CreatePaymentMethod,
  PaymentMethod,
  PaymentMethodKind,
} from '../../../../core/interfaces/payment-method';
import { PaymentMethodService } from '../../../../core/services/payment-method.service';
import { SwalService } from '../../../../core/services/utils/swal.service';
import { AuthService } from '../../../../core/services/utils/auth.service';
import { ReadOnlyNoticeComponent } from '../../../ui/read-only-notice/read-only-notice.component';

const KINDS: PaymentMethodKind[] = ['bank_transfer', 'cash', 'other'];

/** Ligne en cours d'édition — un moyen existant, ou un nouveau. */
type Draft = Omit<PaymentMethod, 'id'> & { id: number | null };

const EMPTY: Draft = {
  id: null,
  code: '',
  kind: 'bank_transfer',
  labelAr: '',
  labelFr: '',
  labelEn: '',
  instructionsAr: null,
  instructionsFr: null,
  instructionsEn: null,
  accountHolder: null,
  bankName: null,
  rib: null,
  iban: null,
  bic: null,
  displayOrder: 1,
  isActive: false,
};

/**
 * Moyens de paiement de l'inscription en ligne.
 *
 * <p>C'est l'écran qui décide de ce qu'un futur élève lit au moment de payer.
 * Un moyen actif est immédiatement visible sur le site vitrine : le serveur
 * refuse d'activer un virement sans RIB ni IBAN, parce qu'annoncer un compte
 * incomplet coûte plus cher que n'annoncer aucun moyen.</p>
 */
@Component({
  selector: 'app-payment-methods',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, ReadOnlyNoticeComponent],
  templateUrl: './payment-methods.component.html',
  styleUrl: './payment-methods.component.css',
})
export class PaymentMethodsComponent implements OnInit, OnDestroy {
  private readonly api = inject(PaymentMethodService);
  private readonly swal = inject(SwalService);
  private readonly translate = inject(TranslateService);
  private readonly destroy$ = new RxJsSubject<void>();

  /**
   * Modifier les moyens de paiement est une permission à part de les lire. Sans `payments.write`, l'écran s'ouvre en consultation : champs
   * figés, gestes d'écriture retirés, et un bandeau qui le dit. Le back
   * revérifie : ce test n'est pas la protection, c'est l'honnêteté de l'écran.
   */
  readonly canWrite = inject(AuthService).hasPermission('payments.write');

  readonly kinds = KINDS;

  methods: PaymentMethod[] = [];
  draft: Draft | null = null;

  isLoading = false;
  hasError = false;
  isSaving = false;

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private load(): void {
    this.isLoading = true;
    this.hasError = false;

    this.api
      .getAll()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: (res) => {
          this.methods = res.data ?? [];
          // L'écran s'ouvre sur le moyen à compléter : dans la pratique, il
          // n'y en a qu'un et il attend son RIB.
          this.draft = this.methods.length > 0 ? this.toDraft(this.methods[0]) : null;
        },
        error: () => (this.hasError = true),
      });
  }

  /** Coordonnées manquantes : le moyen ne peut pas encore être activé. */
  isIncomplete(method: PaymentMethod | Draft): boolean {
    return method.kind === 'bank_transfer' && !method.rib && !method.iban;
  }

  select(method: PaymentMethod): void {
    this.draft = this.toDraft(method);
  }

  addNew(): void {
    this.draft = { ...EMPTY };
  }

  cancel(): void {
    this.draft = this.methods.length > 0 ? this.toDraft(this.methods[0]) : null;
  }

  save(): void {
    if (!this.draft) return;
    const draft = this.draft;

    const payload = {
      kind: draft.kind,
      labelAr: draft.labelAr,
      labelFr: draft.labelFr,
      labelEn: draft.labelEn,
      instructionsAr: this.blankToNull(draft.instructionsAr),
      instructionsFr: this.blankToNull(draft.instructionsFr),
      instructionsEn: this.blankToNull(draft.instructionsEn),
      accountHolder: this.blankToNull(draft.accountHolder),
      bankName: this.blankToNull(draft.bankName),
      rib: this.blankToNull(draft.rib),
      iban: this.blankToNull(draft.iban),
      bic: this.blankToNull(draft.bic),
      displayOrder: Number(draft.displayOrder) || 1,
      isActive: draft.isActive,
    };

    this.isSaving = true;
    const request$ =
      draft.id == null
        ? this.api.create({ ...payload, code: draft.code.trim() } as CreatePaymentMethod)
        : this.api.update(draft.id, payload);

    request$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.isSaving = false)),
      )
      .subscribe({
        next: (res) => {
          const saved = res.data;
          const index = this.methods.findIndex((m) => m.id === saved.id);
          if (index >= 0) this.methods[index] = saved;
          else this.methods = [...this.methods, saved];
          this.draft = this.toDraft(saved);
          this.swal.success(this.translate.instant('PAYMENT_METHODS.SAVED'));
        },
        error: (err) => this.swal.error(this.messageOf(err, 'PAYMENT_METHODS.ERROR_SAVE')),
      });
  }

  remove(method: PaymentMethod): void {
    this.swal.confirm(this.translate.instant('PAYMENT_METHODS.CONFIRM_DELETE'), () => {
      this.api
        .delete(method.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.methods = this.methods.filter((m) => m.id !== method.id);
            if (this.draft?.id === method.id) this.cancel();
            this.swal.success(this.translate.instant('PAYMENT_METHODS.DELETED'));
          },
          error: (err) => this.swal.error(this.messageOf(err, 'PAYMENT_METHODS.ERROR_DELETE')),
        });
    });
  }

  private toDraft(method: PaymentMethod): Draft {
    return { ...method };
  }

  private blankToNull(value: string | null): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  }

  private messageOf(err: unknown, fallbackKey: string): string {
    const message = (err as { error?: { message?: string } })?.error?.message;
    return message || this.translate.instant(fallbackKey);
  }
}
